import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Badge, Button, Empty, Popconfirm, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { getApiError } from '../../api/client';
import type { Series } from '../../api/types';
import { useIsMobile } from '../../lib/responsive';
import { useFirmaCurenta } from '../firme/FirmaCurentaContext';
import { deleteSeries, fetchSeries } from './api';
import { SerieFormDrawer } from './SerieFormDrawer';

const { Title } = Typography;

const DOC_TYPE_LABELS: Record<string, string> = { INVOICE: 'Factură', PROFORMA: 'Proformă' };
const NUMBERING_LOCK_TOOLTIP = 'Seria are facturi emise — proprietățile de numerotare nu mai pot fi modificate.';

/** `/serii` — scoped on the current firm, no server pagination (per brief:
 * `GET /firms/:firmaId/series` returns the full unpaginated array). */
export function SeriiListPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { firmaId } = useFirmaCurenta();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Series | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['series', firmaId],
    queryFn: () => fetchSeries(firmaId as string),
    enabled: !!firmaId,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSeries,
    onSuccess: () => {
      message.success('Seria a fost ștearsă.');
      queryClient.invalidateQueries({ queryKey: ['series', firmaId] });
    },
    onError: (error) => {
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Seria nu a putut fi ștearsă.');
    },
  });

  function openCreate() {
    setEditing(null);
    setDrawerOpen(true);
  }
  function openEdit(series: Series) {
    setEditing(series);
    setDrawerOpen(true);
  }
  function closeDrawer() {
    setDrawerOpen(false);
    setEditing(null);
  }

  const columns: ColumnsType<Series> = [
    { title: 'Prefix', dataIndex: 'prefix' },
    { title: 'Tip', dataIndex: 'docType', render: (v: string) => <Tag>{DOC_TYPE_LABELS[v] ?? v}</Tag> },
    {
      title: 'Următorul număr',
      dataIndex: 'nextNumberPreview',
      align: 'right',
      render: (v: string) => <span className="num">{v}</span>,
    },
    {
      title: 'Facturi emise',
      dataIndex: 'invoiceCount',
      align: 'right',
      render: (v: number) => <span className="num">{v}</span>,
    },
    {
      title: 'Implicită',
      dataIndex: 'isDefault',
      render: (v: boolean) => (v ? <Badge status="success" text="Implicită" /> : null),
    },
    {
      title: 'Stare',
      dataIndex: 'isActive',
      render: (v: boolean) => (v ? <Tag color="green">Activă</Tag> : <Tag>Inactivă</Tag>),
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>
            Editează
          </Button>
          {row.invoiceCount > 0 ? (
            <Tooltip title={NUMBERING_LOCK_TOOLTIP}>
              <Button size="small" danger disabled>
                Șterge
              </Button>
            </Tooltip>
          ) : (
            <Popconfirm title="Ștergi seria?" onConfirm={() => deleteMutation.mutate(row.id)}>
              <Button size="small" danger loading={deleteMutation.isPending && deleteMutation.variables === row.id}>
                Șterge
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3}>Serii</Title>
      {!firmaId ? (
        <Empty description="Selectează sau adaugă mai întâi o firmă." />
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Button type="primary" icon={<PlusOutlined />} block={isMobile} onClick={openCreate}>
              Adaugă serie
            </Button>
          </div>
          <Table<Series> rowKey="id" columns={columns} dataSource={data} loading={isLoading} scroll={{ x: 'max-content' }} pagination={false} />
          <SerieFormDrawer open={drawerOpen} firmaId={firmaId} series={editing} onClose={closeDrawer} />
        </>
      )}
    </div>
  );
}
